import { Link, useLocation } from "react-router-dom";
import { Home, Plus, Search, Ticket, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { useViewMode } from "@/hooks/useViewMode";
import { openCreateEventModal, preloadCreateEventModal } from "@/components/CreateEventModal";
import { useScrollingDown } from "@/hooks/useScrollDirection";
import { useModalOpen } from "@/hooks/useModalOpen";

type NavTheme = {
  /** soft colour wash spread across the whole tile */
  wash: string;
  /** colour lit from within the tile, never bleeding outside it */
  glow: string;
  /** hairline edge so the wash still reads as a tile */
  ring: string;
  /** icon + label tint when active */
  tint: string;
};

const THEMES: Record<string, NavTheme> = {
  home: {
    wash: "bg-gradient-to-br from-violet-500/30 via-indigo-500/12 to-transparent",
    glow: "shadow-[inset_0_0_20px_-4px_rgba(139,92,246,0.55)]",
    ring: "ring-1 ring-inset ring-violet-400/25",
    tint: "text-violet-200",
  },
  create: {
    wash: "bg-gradient-to-br from-emerald-500/30 via-teal-500/12 to-transparent",
    glow: "shadow-[inset_0_0_20px_-4px_rgba(16,185,129,0.55)]",
    ring: "ring-1 ring-inset ring-emerald-400/25",
    tint: "text-emerald-200",
  },
  manage: {
    wash: "bg-gradient-to-br from-amber-500/30 via-orange-500/12 to-transparent",
    glow: "shadow-[inset_0_0_20px_-4px_rgba(245,158,11,0.55)]",
    ring: "ring-1 ring-inset ring-amber-400/25",
    tint: "text-amber-200",
  },
  tickets: {
    wash: "bg-gradient-to-br from-cyan-500/30 via-blue-500/12 to-transparent",
    glow: "shadow-[inset_0_0_20px_-4px_rgba(6,182,212,0.55)]",
    ring: "ring-1 ring-inset ring-cyan-400/25",
    tint: "text-cyan-200",
  },
  browse: {
    wash: "bg-gradient-to-br from-orange-500/30 via-orange-400/12 to-transparent",
    glow: "shadow-[inset_0_0_20px_-4px_rgba(249,115,22,0.55)]",
    ring: "ring-1 ring-inset ring-orange-400/25",
    tint: "text-orange-200",
  },
};

// Soft light pooling from the top of an active tile
const SHEEN =
  "bg-[radial-gradient(120%_85%_at_50%_0%,rgba(255,255,255,0.12),transparent_65%)]";

type NavItem = { path: string; icon: any; label: string; theme: string };

const item = (path: string, icon: any, label: string, theme: string): NavItem => ({
  path,
  icon,
  label,
  theme,
});

// Ordered tab list for the bottom nav. Shared with the swipe-navigation hook
// so swiping always follows the same order the tabs are shown in.
export const getMobileNavItems = (opts: {
  isLoading: boolean;
  isAuthenticated: boolean;
  role: string | null;
  approvalStatus: string | null;
  viewMode: string;
}): NavItem[] => {
  const { isLoading, isAuthenticated, role, approvalStatus, viewMode } = opts;

  // While loading auth, show minimal nav to prevent flicker
  if (isLoading) {
    return [
      item("/events", Search, "Browse", "browse"),
      item("/my-tickets", Ticket, "Tickets", "tickets"),
    ];
  }

  // Not logged in: always show unauthenticated nav regardless of localStorage viewMode
  if (!isAuthenticated) {
    return [
      item("/", Home, "Home", "home"),
      item("/manage", LayoutDashboard, "Manage", "manage"),
      item("/create-event", Plus, "Create", "create"),
      item("/my-tickets", Ticket, "Tickets", "tickets"),
      item("/events", Search, "Browse", "browse"),
    ];
  }

  const isProfessional =
    (role === "organizer" || role === "promoter") &&
    (approvalStatus === "approved" || approvalStatus === "pending");
  const isInExploreMode = viewMode === "explore";

  // If in explore/attendee mode, show attendee navigation
  if (isProfessional && isInExploreMode) {
    return [
      item("/", Home, "Home", "home"),
      item("/my-tickets", Ticket, "Tickets", "tickets"),
      item("/events", Search, "Browse", "browse"),
    ];
  }

  // Nav based on active viewMode
  if (viewMode === "organizer") {
    return [
      item("/", Home, "Home", "home"),
      item("/manage", LayoutDashboard, "Manage", "manage"),
      item("/create-event", Plus, "Create", "create"),
    ];
  }

  // Authenticated attendees
  return [
    item("/", Home, "Home", "home"),
    item("/my-tickets", Ticket, "Tickets", "tickets"),
    item("/events", Search, "Browse", "browse"),
  ];
};

export const MobileBottomNav = () => {
  const location = useLocation();
  const { role, isAuthenticated, isLoading, approvalStatus } = useUserRole();
  const { viewMode } = useViewMode();
  const compact = useScrollingDown();
  const modalOpen = useModalOpen();

  // Hide on token-based check-in page (standalone scanner for staff)
  const searchParams = new URLSearchParams(location.search);
  const isStandaloneScanner = location.pathname.startsWith("/check-in/") && searchParams.has("token");

  if (isStandaloneScanner) {
    return null;
  }

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const allItems = getMobileNavItems({ isLoading, isAuthenticated, role, approvalStatus, viewMode });
  // One primary action floats on its own: Create for organizers, otherwise Browse
  const primaryPath =
    allItems.some((i: any) => i.path === "/create-event") && viewMode === "organizer" && isAuthenticated
      ? "/create-event"
      : "/events";
  const primary: any = allItems.find((i: any) => i.path === primaryPath);
  const tabs = allItems.filter((i: any) => i.path !== primaryPath);

  const handlePrimary = (e: React.MouseEvent) => {
    if (primary?.path === "/create-event") {
      e.preventDefault();
      openCreateEventModal();
    }
  };

  const primaryTheme: NavTheme = THEMES[primary?.theme] ?? THEMES.browse;
  const primaryActive = !!primary && isActive(primary.path);
  const PrimaryIcon = primary?.icon;

  return (
    <div className={cn(
      "md:hidden fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] z-50 flex items-center justify-center gap-3 pointer-events-none transition-all duration-300",
      modalOpen && "invisible opacity-0"
    )}>
      <nav
        aria-label="Main"
        className="pointer-events-auto glass !border-foreground/5 rounded-2xl p-1.5 shadow-2xl shadow-black/50 flex items-center gap-1"
      >
        {tabs.map((tab: any) => {
          const active = isActive(tab.path);
          const Icon = tab.icon;
          const theme: NavTheme = THEMES[tab.theme] ?? THEMES.browse;
          return (
            <Link
              key={tab.path}
              to={tab.path}
              aria-current={active ? "page" : undefined}
              className={cn(
                "relative flex flex-col items-center justify-center overflow-hidden rounded-xl transition-all duration-300 h-12",
                compact ? "w-12" : "w-[clamp(3.2rem,16vw,4.25rem)]",
                active ? cn(theme.wash, theme.glow, theme.ring) : "hover:bg-foreground/5"
              )}
            >
              {active && <span aria-hidden className={cn("pointer-events-none absolute inset-0", SHEEN)} />}
              <Icon
                className={cn(
                  "relative h-5 w-5 transition-colors duration-300",
                  active ? theme.tint : "text-muted-foreground"
                )}
              />
              <span
                className={cn(
                  "relative text-[10px] font-medium transition-all duration-300 overflow-hidden",
                  compact ? "max-h-0 opacity-0" : "max-h-4 opacity-100 mt-1",
                  active ? theme.tint : "text-muted-foreground"
                )}
              >
                {tab.label}
              </span>
            </Link>
          );
        })}
      </nav>
      {primary && PrimaryIcon && (
        <Link
          to={primary.path}
          onClick={handlePrimary}
          onPointerEnter={primary.path === "/create-event" ? preloadCreateEventModal : undefined}
          aria-label={primary.label}
          aria-current={primaryActive ? "page" : undefined}
          className={cn(
            "relative pointer-events-auto shrink-0 overflow-hidden rounded-2xl h-14 w-14 flex items-center justify-center backdrop-blur-xl bg-card/70 border border-foreground/5 transition-all duration-300",
            primaryActive
              ? cn(primaryTheme.wash, primaryTheme.glow, primaryTheme.ring)
              : "shadow-2xl shadow-black/50"
          )}
        >
          {primaryActive && (
            <span aria-hidden className={cn("pointer-events-none absolute inset-0", SHEEN)} />
          )}
          <PrimaryIcon
            className={cn(
              "relative h-6 w-6 transition-colors duration-300",
              primaryActive ? primaryTheme.tint : "text-muted-foreground"
            )}
          />
        </Link>
      )}
    </div>
  );
};
